"use client";

import { TFLink } from "@/Web/Components/TFLink/TFLink";
import { useDirection } from "@/utils/useDirection";
import { useTranslations } from "next-intl";
import {
  FaFacebook,
  FaTwitter,
  FaYoutube,
  FaTelegram,
  FaHeart,
} from "react-icons/fa";
import TFLayoutFooterBrands from "./TFLayoutFooterBrands";

type SocialLink = {
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  gradient: string;
};

type Link = {
  title: string;
  href: string;
};
type Section = {
  title: string;
  links: Link[];
};

const socialLinks: SocialLink[] = [
  { icon: FaFacebook, href: "#", gradient: "from-blue-600 to-blue-800" },
  { icon: FaTwitter, href: "#", gradient: "from-sky-400 to-sky-600" },
  { icon: FaYoutube, href: "#", gradient: "from-red-500 to-red-700" },
  { icon: FaTelegram, href: "#", gradient: "from-blue-400 to-blue-600" },
];

// Компонент градиентного логотипа
const FooterLogo = () => {
  return (
    <div
      className="text-xl font-bold mb-2"
      style={{ textShadow: "0px 0px 5px rgba(0, 0, 0, 0.2)" }}
    >
      TIFERET.APP
    </div>
  );
};

const Section = (section: Section) => {
  return (
    <div className="space-y-4 lg:col-span-1">
      <div className="font-bold text-black/80 dark:text-black/20 font-sans uppercase text-xs tracking-wider mb-4">
        {section.title}
      </div>
      <div className="space-y-3">
        {section.links.map((item) => (
          <SectionLink key={item.title} title={item.title} href={item.href} />
        ))}
      </div>
    </div>
  );
};

const SectionLink = ({ title, href }: Link) => {
  const direction = useDirection();
  const isRTL = direction === "rtl";

  return (
    <div>
      <TFLink
        href={href}
        className="group relative text-black/60 hover:text-black/80 dark:text-black/40 dark:hover:text-black/60 transition-all duration-300 text-sm leading-relaxed inline-block"
      >
        <span className="relative z-10">{title}</span>
        <span
          className={`absolute bottom-0 ${isRTL ? "right-0" : "left-0"} w-0 h-0.5 ${isRTL ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-black/30 to-black/80 group-hover:w-full transition-all duration-300`}
        ></span>
      </TFLink>
    </div>
  );
};

const TFLayoutFooter = () => {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  const t = useTranslations("web.components.TFFooter");

  const sections: Section[] = [
    {
      title: t("sections.tiferetHub"),
      links: [
        { title: t("links.aboutTiferet"), href: "/platform" },
        { title: t("links.tiferetHub"), href: "/platform/hub" },
        { title: t("links.features"), href: "/platform/hub/features" },
        { title: t("links.extensions"), href: "/platform/hub/extensions" },
      ],
    },
    {
      title: t("sections.resources"),
      links: [
        { title: t("links.news"), href: "/news" },
        { title: t("links.articles"), href: "/articles" },
        { title: t("links.library"), href: "/library" },
        { title: t("links.events"), href: "/events" },
        { title: t("links.donations"), href: "/donations" },
      ],
    },
    {
      title: t("sections.help"),
      links: [
        { title: t("links.faq"), href: "/help/faq" },
        { title: t("links.contacts"), href: "/help/contacts" },
        { title: t("links.featureRequests"), href: "/help/features" },
        { title: t("links.systemStatus"), href: "/help/status" },
      ],
    },
  ];

  const bottomLinks: Link[] = [
    { title: t("legal.privacy"), href: "/legal/privacy" },
    { title: t("legal.terms"), href: "/legal/terms" },
    { title: t("legal.cookies"), href: "/legal/cookies" },
    { title: t("legal.communityStandards"), href: "/help/community-standards" },
  ];

  return (
    <footer className="relative bg-black/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
        {/* Секции футера */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-8 ${isRTL ? "text-right" : ""}`}
        >
          {/* Секция с социальными сетями */}
          <div className="space-y-6 lg:col-span-1">
            <FooterLogo />
            <div className="space-y-4">
              <p className="text-sm text-black/60 dark:text-black/40 leading-relaxed">
                {t("description")}
              </p>
              <div className="flex gap-4">
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <TFLink
                      key={index}
                      href={social.href}
                      className="group relative transition-all duration-300"
                    >
                      <IconComponent className="w-6 h-6 text-black/50 dark:text-black/40 opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
                    </TFLink>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Остальные секции */}
          {sections.map((section, index) => (
            <Section key={index} title={section.title} links={section.links} />
          ))}
        </div>

        <TFLayoutFooterBrands />

        {/* Нижняя часть */}
        <div className="flex flex-wrap justify-center gap-6 mt-8 mb-4">
          {bottomLinks.map((item) => (
            <TFLink
              key={item.title}
              href={item.href}
              className="text-black/50 hover:text-black/70 dark:text-black/40 dark:hover:text-black/60 transition-colors duration-200 text-sm relative group"
            >
              {item.title}
              <span
                className={`absolute -bottom-1 ${isRTL ? "right-0" : "left-0"} w-0 h-0.5 bg-black/40 group-hover:w-full transition-all duration-300`}
              ></span>
            </TFLink>
          ))}
        </div>

        <div className="flex items-center justify-center gap-2 text-black/50 dark:text-black/40 text-sm">
          <span>{t("madeWith")}</span>
          <FaHeart className="text-red-500 animate-pulse" />
          <span>{t("inIsrael")}</span>
        </div>
      </div>
    </footer>
  );
};

export default TFLayoutFooter;
